<template>
  <div class="wallet-billing-page">
    <b-card
      no-body
      class="billing-card"
    >
      <div class="billing-toolbar">
        <div class="toolbar-left">
          <b-form-select
            v-model="selectedCount"
            :options="countOptions"
            class="count-select"
          />
          <b-button
            class="upgrade-btn"
            @click="openBillingDetails"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            <span>Upgrade Plan</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            class="manage-payment-btn"
            :disabled="billingPortalLoading"
            @click="openBillingPortal"
          >
            <b-spinner
              v-if="billingPortalLoading"
              small
              class="mr-50"
            />
            Manage payment methods
          </b-button>
        </div>

        <div class="toolbar-right">
          <b-form-input
            v-model="search"
            placeholder="Search"
            class="search-input"
          />
          <b-dropdown
            right
            variant="outline-secondary"
            :text="selectedFilter"
            class="select-dropdown"
          >
            <b-dropdown-item @click="selectedFilter = 'Select'">
              Select
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Success'">
              Success
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Pending'">
              Pending
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Failed'">
              Failed
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Paid'">
              Paid
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        responsive
        class="billing-table mb-0"
        :items="filteredRows"
        :fields="tableFields"
      >
        <template #cell(status)="{ item }">
          <span
            class="status-badge"
            :class="`status-${item.statusKey}`"
          >
            {{ item.status }}
          </span>
        </template>

        <template #cell(action)="{ item }">
          <button
            type="button"
            class="download-btn"
            :disabled="!item.invoiceUrl"
            @click="downloadInvoice(item)"
          >
            <img
              src="/svg/wallet/Download.svg"
              alt="Download"
              class="download-btn-icon"
            >
          </button>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormSelect,
  BButton,
  BFormInput,
  BDropdown,
  BDropdownItem,
  BTable,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'

export default {
  components: {
    BCard,
    BFormSelect,
    BButton,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BTable,
  },
  data() {
    return {
      selectedCount: '08',
      selectedFilter: 'Select',
      search: '',
      countOptions: ['08', '10', '20', '50'],
      tableFields: [
        { key: 'date', label: 'DATE' },
        { key: 'plan', label: 'PLAN' },
        { key: 'transactionId', label: 'TRANSACTION ID' },
        { key: 'expireDate', label: 'EXPIRE DATE' },
        { key: 'paymentMethod', label: 'PAYMENT METHOD' },
        { key: 'status', label: 'STATUS' },
        { key: 'action', label: '' },
      ],
      rows: [
        
      ],
      billingPortalLoading: false,
    }
  },
  mounted() {
    this.fetchWalletHistory()
  },
  computed: {
    filteredRows() {
      const term = this.search.trim().toLowerCase()

      return this.rows.filter(item => {
        const byFilter = this.selectedFilter === 'Select' || item.status === this.selectedFilter
        const bySearch = !term || `${item.date} ${item.plan} ${item.transactionId} ${item.expireDate} ${item.paymentMethod} ${item.status}`.toLowerCase().includes(term)
        return byFilter && bySearch
      })
    },
  },
  methods: {
    async fetchWalletHistory() {
      try {
        const response = await vendorPanelApi.walletHistory({
          page: 1,
          per_page: Number(this.selectedCount),
          search: this.search,
          status: this.selectedFilter === 'Select' ? '' : this.selectedFilter,
        })
        const payload = response?.data?.data || {}
        const list = payload.data || []
        this.rows = list.map(item => ({
          date: item.date,
          plan: item.plan_name,
          transactionId: item.transaction_id,
          expireDate: item.expire_date,
          paymentMethod: item.payment_method,
          status: item.status,
          statusKey: String(item.status || '').toLowerCase(),
          invoiceUrl: item.invoice_url,
        }))
      } catch (error) {
        this.rows = []
        this.$toast?.error?.(error?.response?.data?.message || 'Unable to load billing history.')
      }
    },
    async openBillingPortal() {
      this.billingPortalLoading = true
      try {
        const { data } = await vendorPanelApi.createBillingPortalSession()
        const portalUrl = data?.data?.portal_url
        if (portalUrl) {
          window.location.href = portalUrl
          return
        }
        this.$toast?.error?.('Billing portal URL was not returned.')
      } catch (error) {
        this.$toast?.error?.(error?.response?.data?.message || 'Unable to open billing portal.')
      } finally {
        this.billingPortalLoading = false
      }
    },
    openBillingDetails() {
      this.$router.push({ name: 'billing-details' })
    },
    downloadInvoice(item) {
      if (!item?.invoiceUrl) return
      window.open(item.invoiceUrl, '_blank', 'noopener,noreferrer')
    },
  },
  watch: {
    selectedCount() {
      this.fetchWalletHistory()
    },
    selectedFilter() {
      this.fetchWalletHistory()
    },
    search() {
      this.fetchWalletHistory()
    },
  },
}
</script>

<style scoped lang="scss">
.wallet-billing-page {
  .billing-card {
    border: 1px solid #ececf3;
    border-radius: 8px;
    box-shadow: none;
    overflow: hidden;
  }

  .billing-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
    padding: 0.9rem 1rem 0.8rem;
  }

  .toolbar-left,
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .count-select {
    width: 70px;
  }

  .upgrade-btn {
    background: #011425 !important;
    border-color: #011425 !important;
    color: #fff !important;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-weight: 600;
    padding: 0.5rem 0.95rem;
  }

  .manage-payment-btn {
    font-weight: 600;
    white-space: nowrap;
    padding: 0.5rem 0.95rem;
  }

  .search-input {
    width: 170px;
    height: 35px;
  }

  .select-dropdown ::v-deep .btn {
    min-width: 95px;
    height: 35px;
  }

  .billing-table {
    ::v-deep thead th {
      color: #787389;
      font-size: 0.78rem;
      font-weight: 700;
      border-top: 1px solid #ececf3;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
      background: #fff;
    }

    ::v-deep tbody td {
      color: #5f5a73;
      font-size: 0.92rem;
      vertical-align: middle;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
    }
  }

  .status-badge {
    font-size: 0.72rem;
    font-weight: 700;
    border-radius: 4px;
    padding: 0.18rem 0.44rem;
    display: inline-flex;
    line-height: 1.1;
  }

  .status-success,
  .status-paid {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.16);
  }

  .status-pending {
    color: #6f6b86;
    background: #e8e8ef;
  }

  .status-failed {
    color: #ea5455;
    background: rgba(234, 84, 85, 0.16);
  }

  .download-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid #111827;
    background: #fff;
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: default;
  }

  .download-btn-icon {
    width: 14px;
    height: 14px;
    object-fit: contain;
    display: block;
  }
}
</style>
